<template>
  <div class="sale-profile">
    <spinner v-if="!loaded" />

    <template v-else>
      <div class="top">
        <align flex>
          <div>
            <icon-button
              tooltip="Close card"
              icon="close"
              @click="close"
            />
          </div>

          <align small-gap>
            <verified-icon
              v-if="sale.cardId || sale.memberVerified"
              v-tooltip="{ content: 'Verified Sale', delay: { show: 1500, hide: 100 }, placement: 'bottom' }"
            />

            <icon-button
              v-if="isStaff && !sale.cardId"
              tooltip="Add to Ladder"
              icon="add_circle"
              @click="adminAddCard"
            />

            <icon-button
              tooltip="Add to Collection"
              icon="bookmark_add"
              @click="addToCollection"
            />

            <icon-button
              v-if="showViewSaleLink"
              tooltip="View sale"
              icon="launch"
              @click="openSale"
            />

            <icon-button
              v-if="isStaff && sale.platform === 'eBay'"
              icon="pageview"
              tooltip="View sale on Terapeak Research"
              @click.stop.prevent="viewTerapeak"
            />

            <icon-button
              v-if="isStaff && viewSalesOptions.length"
              tooltip="Remove Graded Profile Info"
              icon="remove_circle_outline"
              @click="adminRemoveGradedInfo(sale.itemId)"
            />

            <icon-button
              v-if="isStaff"
              tooltip="Edit Sale"
              icon="edit"
              @click="editSale"
            />

            <icon-button
              v-if="isStaff && !sale.cardId"
              tooltip="Delete sale"
              icon="delete_outline"
              @click.stop.prevent="adminDeleteSale(sale)"
            />
          </align>
        </align>
      </div>

      <section class="details">
        <div v-if="sale.image" class="image-container" @click.stop.prevent="showFullscreenImage = true">
          <img :src="sale.image" :alt="sale.title" />
        </div>

        <div class="stats">
          <div class="platform-text">
            <span>{{ platform }}</span><span v-if="sale.seller"> - {{ sale.seller }}</span>
          </div>

          <h5>{{ sale.title }}</h5>

          <section class="stats-grid">
            <stat-item
              label="Sold Price"
              :value="$filters.currency(sale.price, { full: true })"
            />

            <stat-item label="Sold Date" :value="$filters.date(sale.date)" />

            <value-display
              v-if="showValueDisplay"
              subtle
              :loading="loadingEstimateValue"
              :confidence="guideValue.confidence"
              :value="guideValue.value"
              :blur="guideValue.confidence === 1"
            />

            <stat-item label="Listing Type" :value="listingType" />

            <stat-item
              v-if="showPop"
              label="Pop"
              :value="populationValue"
              :has-empty-value="!populationValue"
            >
              <template #after>
                <icon-button
                  class="pop-report"
                  icon="menu_book"
                  tooltip="View in depth population report"
                  @click="showPopData"
                />
              </template>
            </stat-item>

            <stat-item v-if="sale.feedback != null" label="Feedback" :value="$filters.number(sale.feedback)" />
          </section>

          <button-dropdown
            v-if="viewSalesOptions.length"
            label="View Sales of this Card"
            align="left"
            kind="secondary"
            block
            :options="viewSalesOptions"
            @click="handleViewSales"
          />
        </div>
      </section>

      <section v-if="card && sale.cardId" class="sale-profile-section">
        <align flex class="ladder-card-header">
          <h4 class="secondary-font">
            Ladder Card
          </h4>

          <icon-button
            v-if="isStaff"
            icon="delete"
            tooltip="Remove Sale from Verified Profile"
            :loading="removingLadderCard"
            @click="removeLadderCard"
          />
        </align>

        <div class="ladder-container">
          <card-list-item
            :card="card"
            :fields="['currentValue']"
            :to="{ query: ladderCardQuery }"
            as="router-link"
          />
        </div>
      </section>

      <section v-if="profile" class="sale-profile-section">
        <h4 class="secondary-font">
          Profile
        </h4>

        <div class="profile-item-container" @click="openProfile">
          <profile-list-item :profile="profile" />
        </div>
      </section>

      <section v-if="index" class="sale-profile-section">
        <align flex class="ladder-card-header">
          <h4 class="secondary-font">
            Index
          </h4>

          <icon-button
            v-if="isStaff && !sale.cardId"
            icon="edit"
            tooltip="Manually set index on this sale"
            @click="showIndexEditor = !showIndexEditor"
          />
        </align>

        <index-search
          v-if="showIndexEditor"
          @selectPlayer="setPlayerIndex"
          @selectIndex="setCategoryIndex"
        />

        <div class="index-item-container" @click="openIndex">
          <index-list-item :index="index" />
        </div>
      </section>

      <section v-if="isStaff" class="sale-profile-section verify">
        <card-search
          v-if="!sale.cardId"
          include-hidden
          placeholder="Assign to a verified card"
          @select="adminVerifySale"
        />

        <br v-if="!sale.cardId" />

        <btn
          :loading="refreshingProfileInfo"
          loading-text="Refreshing"
          block
          kind="secondary"
          @click="refreshProfileInfo"
        >
          Refresh Profile Info
        </btn>
      </section>

      <section v-if="sale.notes" class="sale-profile-section notes">
        <h4 class="secondary-font">
          Notes
        </h4>
        <p>{{ sale.notes }}</p>
      </section>
    </template>

    <fullscreen-image
      :show="showFullscreenImage"
      :image="sale.originalImage || sale.image"
      :label="sale.title"
      @hide="showFullscreenImage = false"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import * as Sentry from '@sentry/vue';
import firebase from 'firebase/compat/app';
import moment from 'moment';

import { saleService } from '@/services';
import { getTerapeakLink } from '@cardladder/shared/utils/format.util';
import { buildExternalSaleOpenUrl } from '@cardladder/shared/utils/sales.util';
import { applySaleTitleToCollectionCard } from '@cardladder/shared/utils/collectionCardTitle.util';
import { LISTING_TYPE_OPTIONS, SELLING_PLATFORM_OPTIONS } from '@cardladder/shared/constants';

import ButtonDropdown from '@cardladder/shared/components/ButtonDropdown';
import VerifiedIcon from '@cardladder/shared/components/VerifiedIcon';
import FullscreenImage from '@cardladder/shared/components/FullscreenImage';
import IndexListItem from '@cardladder/shared/components/IndexListItem';
import CardListItem from '@cardladder/shared/components/CardListItem';
import IndexSearch from '@/components/IndexSearch';
import StatItem from '@cardladder/shared/components/StatItem';
import ValueDisplay from '@cardladder/shared/components/ValueDisplay';
import ProfileListItem from '@/profiles/ProfileListItem';

import CardSearch from '@/cards/CardSearch';

const THREE_MONTHS_AGO = moment().subtract(3, 'months').startOf('day').toDate();
const SIDE_PANEL_QUERY_KEYS = ['cardId', 'saleId', 'profileId', 'postId', 'showcaseCardId', 'showSales'];

export default {
  components: {
    ButtonDropdown,
    VerifiedIcon,
    FullscreenImage,
    IndexListItem,
    CardListItem,
    StatItem,
    ValueDisplay,
    IndexSearch,
    CardSearch,
    ProfileListItem,
  },

  props: {
    id: String,
  },

  data() {
    return {
      sale: {},
      loaded: false,
      loadingEstimateValue: false,
      showFullscreenImage: false,
      index: null,
      profile: null,
      population: null,
      guideValue: null,
      removingLadderCard: false,
      showIndexEditor: false,
      card: null,
      refreshingProfileInfo: false,
    };
  },

  created() {
    this.load();
  },

  watch: {
    id(to, from) {
      if (to !== from) {
        this.load();
      }
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    ...mapGetters({
      isStaff: 'isStaff',
    }),

    listingType() {
      let option = LISTING_TYPE_OPTIONS.find(({ value }) => value === this.sale.listingType);

      if (option) {
        return option.label;
      }

      return '';
    },

    platform() {
      let option = SELLING_PLATFORM_OPTIONS.find(({ value }) => value === this.sale.platform);

      if (option) {
        return option.label;
      }

      return '';
    },

    populationValue() {
      if (this.card && this.card.pop) {
        return this.$filters.number(this.card.pop);
      }

      if (this.population) {
        return this.$filters.number(this.population);
      }

      return null;
    },

    isEbaySale() {
      return this.sale?.platform === 'eBay';
    },

    showValueDisplay() {
      return this.guideValue && this.guideValue.value && (!this.user || !this.user.hideSalesHistoryValue);
    },

    showViewSaleLink() {
      return Boolean(this.sale.url) && (this.sale.platform !== 'eBay' || this.sale.date > THREE_MONTHS_AGO);
    },

    showPop() {
      return Boolean(this.sale.profileId);
    },

    ladderCardQuery() {
      if (!this.card) {
        return null;
      }

      const query = { ...this.$route.query, cardId: this.card.id };

      delete query.saleId;

      return query;
    },

    viewSalesOptions() {
      const options = [];

      if (this.sale.profileId && this.profile && this.profile.hasMatches) {
        options.push({ value: 'all', label: 'All Matches' });
      }

      if (this.sale.profileId && this.sale.grader && this.sale.grade) {
        options.push({ value: 'grader', label: `All ${this.sale.grader.toUpperCase()} Sales` });
        options.push({ value: 'grade', label: 'Match Grade' });
      }

      return options;
    },
  },

  methods: {
    openProfileDestination(profileId) {
      if (!profileId) return;

      const isRightPanelContext = SIDE_PANEL_QUERY_KEYS.some((key) => Boolean(this.$route.query[key]));

      if (isRightPanelContext) {
        const query = { ...this.$route.query, profileId };
        delete query.saleId;
        delete query.index;
        this.$router.push({ query });
      } else {
        this.$router.push(`/profiles/${profileId}`);
      }
    },

    async load() {
      try {
        this.loaded = false;
        this.sale = {};
        this.index = null;
        this.guideValue = null;
        this.card = null;
        this.profile = null;

        if (!this.id) {
          this.close();
          return;
        }

        const { data: saleProfile } = await firebase.functions().httpsCallable('httpsaleprofile')({ saleId: this.id });
        const rawSale = saleProfile.sale || {};

        this.sale = {
          ...rawSale,
          date: rawSale.date != null && typeof rawSale.date === 'string' ? new Date(rawSale.date) : rawSale.date,
        };

        this.card = saleProfile.card || null;
        this.index = saleProfile.index || null;
        this.profile = saleProfile.profile || null;
        this.population = saleProfile.population || null;

        if (saleProfile.guideValue && typeof saleProfile.guideValue.value === 'number') {
          this.guideValue = {
            ...saleProfile.guideValue,
            value: this.$filters.currency(saleProfile.guideValue.value, { full: true }),
          };
        } else {
          this.guideValue = saleProfile.guideValue || null;
        }

        this.loaded = true;

        if (!this.sale.cardId && this.sale.profileId && this.sale.grader && this.sale.grade) {
          const baseGuideValue = this.guideValue;

          this.loadingEstimateValue = true;
          this.guideValue = baseGuideValue ? { ...baseGuideValue, value: null } : { confidence: 1, value: null };

          (async() => {
            try {
              const request = {
                profileId: this.sale.profileId,
                grader: this.sale.grader,
                grade: this.sale.grade,
                qualifierType: this.sale.qualifierType || null,
                autographGrade: this.sale.autographGrade || null,
                description: this.sale.label || this.sale.title,
              };

              const { data: estimateResponse } = await firebase.functions().httpsCallable('httpcardestimate')(request);

              if (estimateResponse && estimateResponse.estimatedValue) {
                this.guideValue = {
                  confidence: estimateResponse.confidence,
                  value: this.$filters.currency(estimateResponse.estimatedValue),
                };
              }
            } catch (e) {
              this.guideValue = baseGuideValue;
            } finally {
              this.loadingEstimateValue = false;
            }
          })();
        }
      } catch (e) {
        const isNotFound = e?.code === 'functions/not-found' || e?.response?.status === 404;
        if (isNotFound) {
          this.close();
        } else {
          Sentry.captureException(e);
        }
      }

      this.loadingEstimateValue = false;
    },

    addToCollection() {
      let card = {};

      if (this.sale.cardId && this.card) {
        card = {
          cardId: this.sale.cardId,
          label: this.card.label,
          image: this.card.image,
          player: this.card.player,
          playerIndexId: this.card.playerIndexId || null,
          set: this.card.set || '',
          variation: this.card.variation || '',
          year: this.card.year || '',
          number: this.card.number || '',
          category: this.card.category,
          condition: this.card.condition,
          pop: this.card.pop || null,
          currentValue: this.card.stats?.currentValue || null,
          marketValue: this.card.stats?.marketValue || null,
          guideValue: this.card.stats?.guideValue || null,
        };
      } else {
        if (this.index?.player) {
          card.player = this.index.player;
          card.playerIndexId = this.index.id;
        } else {
          card.otherIndexId = this.index.id;
        }

        if (this.sale.profileId && this.sale.grade && this.sale.grader) {
          card.profileId = this.sale.profileId;
          card.grader = this.sale.grader;
          card.grade = this.sale.grade;
          card.qualifierType = this.sale.qualifierType;
          card.autographGrade = this.sale.autographGrade;
          card.hasQualifier = Boolean(this.sale.qualifierType);
          card.hasAutographGrade = Boolean(this.sale.autographGrade);
          card.useGradedCardAutomatic = true;
        } else {
          card.playerIndexPrice = this.sale.price;
          card.playerIndexDate = this.sale.date;
        }

        if (this.sale.image) {
          card.imageUrl = this.sale.image;
        }

        if (this.sale.cert) {
          card.cert = this.sale.cert;
        }

        applySaleTitleToCollectionCard(card, this.sale.cardDescription || this.sale.title);
      }

      this.$bus.$emit('collection-add-card', {
        ...card,
        datePurchased: this.sale.date,
        investment: this.sale.price,
      });
    },

    adminAddCard() {
      const card = {};

      if (this.index?.player) {
        card.player = this.index.player;
        card.playerIndexId = this.index.id;
      }

      if (this.sale.image) {
        card.imageUrl = this.sale.image;
      }

      this.$bus.$emit('admin-copy-card', card);
    },

    editSale() {
      this.$bus.$emit('sales-history-item-edit', this.sale);
    },

    async setCategoryIndex(id) {
      try {
        this.showIndexEditor = false;

        await saleService.updateSalesHistorySale({
          itemId: this.sale.itemId,
          playerIndexId: null,
          categoryIndexId: id,
          ignoreIndex: false,
          sendToPending: false,
        });

        setTimeout(() => {
          this.load();
        }, 3000);
      } catch (e) {
        this.$toasted.error('Error setting the manual index');
      }
    },

    async setPlayerIndex({ id }) {
      try {
        this.showIndexEditor = false;

        await saleService.updateSalesHistorySale({
          itemId: this.sale.itemId,
          playerIndexId: id,
          categoryIndexId: null,
          ignoreIndex: false,
          sendToPending: false,
        });

        setTimeout(() => {
          this.load();
        }, 3000);
      } catch (e) {
        this.$toasted.error('Error setting the manual index');
      }
    },

    async removeLadderCard() {
      if (window.confirm('Are you sure you want to remove this sale from the verified profile?')) {
        try {
          this.removingLadderCard = true;
          await saleService.deleteSale(this.sale.cardId, this.sale.itemId);
          this.$toasted.success('Sale has been removed from verified profile!');

          setTimeout(() => {
            this.load();
          }, 5000);
        } catch (e) {
          this.$toasted.error('Error removing the verified sale from the profile');
          Sentry.captureException(e);
        }

        this.removingLadderCard = false;
      }
    },

    async adminDeleteSale(item) {
      if (window.confirm('Are you sure you want to delete this sale?')) {
        try {
          await saleService.deleteArchiveSale(item);
          this.$toasted.success('Deleted the sale!');
          this.close();
        } catch (e) {
          this.$toasted.error('Error deleting the sale');
          Sentry.captureException(e);
        }
      }
    },

    async adminRemoveGradedInfo(itemId) {
      if (window.confirm('Are you sure you want to remove graded profile info from this sale?')) {
        try {
          await firebase.functions().httpsCallable('httpRemoveGradedInfoFromSale')({ itemId });
          this.$toasted.success('Removed the info!');
          this.load();
        } catch (e) {
          this.$toasted.error('Error removing the info');
          Sentry.captureException(e);
        }
      }
    },

    async refreshProfileInfo() {
      try {
        this.refreshingProfileInfo = true;

        await firebase.firestore().collection('salesArchiveCerts').add({
          cacheOverride: true,
          sales: [{
            image: this.sale.image || null,
            title: this.sale.title || null,
            id: this.sale.itemId,
          }],
        });

        this.$toasted.success('Submitted the request to refresh the profile info!');
      } catch (e) {
        this.$toasted.error('Error refreshing the profile info');
        Sentry.captureException(e);
      }

      this.refreshingProfileInfo = false;
    },

    async adminVerifySale({ id }) {
      try {
        const sale = {
          itemId: this.sale.itemId || id,
          url: this.sale.url || '',
          image: this.sale.image || '',
          title: this.sale.title || '',
          date: this.sale.date,
          price: this.sale.price,
          platform: this.sale.platform || '',
          listingType: this.sale.listingType || '',
          notes: this.sale.notes || '',
        };

        await saleService.addSale(id, sale);
        this.$toasted.success('Sale has been added to verified card!');

        setTimeout(() => {
          this.load();
        }, 5000);
      } catch (e) {
        this.$toasted.error('Error verifying the sale');
        Sentry.captureException(e);
      }
    },

    handleViewSales(option) {
      const filters = {
        profileId: `${this.sale.profileId}:${this.sale.label}`,
      };

      if (option === 'all') {
        filters.profileId = `${this.profile.id}:${this.sale.label}`;
      }

      if (option === 'grader' || option === 'grade') {
        filters.grader = this.sale.grader;
      }

      if (option === 'grade') {
        filters.grade = this.sale.grade;
        filters.qualifierType = this.sale.qualifierType;
        filters.autographGrade = this.sale.autographGrade;
      }

      if (this.$route.name === 'SalesHistory') {
        this.$bus.$emit('search-card-sales', filters);
      } else {
        let query = { saleId: this.sale.id };
        let filterArray = [];

        if (filters.profileId) {
          filterArray.push(`profileId:${this.sale.profileId}`);
        }

        if (filters.grader) {
          filterArray.push(`grader:${this.sale.grader}`);
        }

        if (filters.grade) {
          filterArray.push(`grade:${this.sale.grade}`);
        }

        if (filters.qualifierType) {
          filterArray.push(`qualifierType:${this.sale.qualifierType}`);
        }

        if (filters.autographGrade) {
          filterArray.push(`autographGrade:${this.sale.autographGrade}`);
        }

        query.filters = filterArray.join('|');

        this.$router.push({ path: '/sales-history', query });
      }

      if (window.innerWidth < 768) {
        this.$nextTick(() => {
          this.close();
        });
      }
    },

    close() {
      const query = { ...this.$route.query };
      delete query.saleId;
      delete query.index;
      this.$router.push({ query });
    },

    openSale() {
      const saleUrl = buildExternalSaleOpenUrl(this.sale);
      if (saleUrl) window.open(saleUrl, '_blank');
    },

    showPopData() {
      this.openProfileDestination(this.profile ? this.profile.id : this.sale.profileId);
    },

    openIndex() {
      if (this.index.player) {
        this.$router.push(`/players/${this.index.player}`);
      } else {
        this.$router.push(`/indexes/${this.index.id}`);
      }
    },

    viewTerapeak() {
      window.open(getTerapeakLink(this.sale.title, true), '_blank');
    },

    openProfile() {
      this.openProfileDestination(this.profile?.id);
    },
  },
};
</script>

<style lang="scss" scoped>
.sale-profile {
  min-height: 100%;
  position: relative;
  padding: $gp;
  --logo-height: 24px;

  h5 {
    margin-bottom: $gp * .5;
  }
}

.details {
  margin-bottom: $gp * 1.5;
}

.image-container {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--theme-highlight);
  padding: $gp 80px;
  border-radius: $border-radius;
  height: 360px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.stats {
  margin-top: $gp;
}

.platform-text {
  font-size: 12px;
  text-transform: uppercase;
  opacity: 0.5;
  letter-spacing: 1px;
}

.sale-profile-section {
  padding-top: $gp;
  border-top: solid 1px var(--theme-border);
  margin-bottom: $gp * 1.5;
}

.ladder-card-header {
  margin-bottom: $gp;

  h4 {
    margin-bottom: 0;
  }
}

.notes {
  margin-top: $gp;
}

.verify {
  margin-bottom: $gp * 2;
}

.stats-grid {
  display: grid;
  grid-gap: $gp;
  grid-template-columns: repeat(3, 1fr);
  margin: $gp 0;

  @media($medium-down) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.index-item-container, .profile-item-container {
  cursor: pointer;
  margin: 0 (-$gp);

  &:hover {
    background-color: var(--theme-highlight);
  }
}

.top {
  margin-bottom: $gp;
}

.ladder-container {
  margin: 0 (-$gp);
}

.list {
  margin: 0 (-$gp);
}

.guide-value {
  margin-top: $gp;

  .description {
    font-size: 18px;
    margin-bottom: $gp;
  }
}
</style>
